import { useEffect, useState } from 'react';
import { searchTags } from '../services/tags.service';
import { searchVideos } from '../services/video.service';
import { TagDto } from '../types/admin.types';
import { Video } from '../types/video.types';
import { parseSearchInput } from '../utils/video-search';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;
const VIDEO_LIMIT = 6;
const TAG_LIMIT = 6;
const DERIVED_LIMIT = 4;

type GlobalSearchStatus = 'idle' | 'loading' | 'success' | 'error';

export interface GlobalSearchSuggestions {
    status: GlobalSearchStatus;
    videos: Video[];
    tags: TagDto[];
    fighters: string[];
    events: string[];
}

const emptySuggestions: GlobalSearchSuggestions = {
    status: 'idle',
    videos: [],
    tags: [],
    fighters: [],
    events: []
};

const getNormalizedKey = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const addUniqueValue = (items: string[], value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed) {
        return;
    }

    const normalized = getNormalizedKey(trimmed);
    if (!normalized || items.some((item) => getNormalizedKey(item) === normalized)) {
        return;
    }

    items.push(trimmed);
};

const buildDerivedFighters = (videos: Video[], query: string) => {
    const normalizedQuery = getNormalizedKey(query);
    const fighters: string[] = [];

    videos.forEach((video) => {
        (video.Participants ?? []).forEach((participant) => {
            const name = participant.ParticipantName;
            if (!normalizedQuery || getNormalizedKey(name).includes(normalizedQuery)) {
                addUniqueValue(fighters, name);
            }
        });
    });

    return fighters.slice(0, DERIVED_LIMIT);
};

const buildDerivedEvents = (videos: Video[], query: string) => {
    const normalizedQuery = getNormalizedKey(query);
    const events: string[] = [];

    videos.forEach((video) => {
        const eventName = video.EventName;
        if (!normalizedQuery || getNormalizedKey(eventName).includes(normalizedQuery)) {
            addUniqueValue(events, eventName);
        }
    });

    return events.slice(0, DERIVED_LIMIT);
};

/**
 * Loads mobile-first global search suggestions from public video and tag feeds.
 */
export const useGlobalSearchSuggestions = (query: string): GlobalSearchSuggestions => {
    const [suggestions, setSuggestions] = useState<GlobalSearchSuggestions>(emptySuggestions);

    useEffect(() => {
        const trimmedQuery = query.trim();
        const parsedInput = parseSearchInput(trimmedQuery);
        const textQuery = parsedInput.query.trim();
        const tagFilters = parsedInput.tags;
        const activeTagQuery = parsedInput.activeTagQuery?.trim() ?? '';
        const tagSuggestionQuery = activeTagQuery || textQuery;
        const hasSearchContext = textQuery.length >= MIN_QUERY_LENGTH
            || tagFilters.length > 0
            || tagSuggestionQuery.length >= MIN_QUERY_LENGTH;

        if (!hasSearchContext) {
            setSuggestions(emptySuggestions);
            return;
        }

        let isActive = true;
        setSuggestions((current) => ({
            ...current,
            status: 'loading'
        }));

        const handler = window.setTimeout(() => {
            const loadSuggestions = async () => {
                const [videoResult, tagResult] = await Promise.allSettled([
                    searchVideos(textQuery, 1, VIDEO_LIMIT, tagFilters),
                    searchTags(tagSuggestionQuery || null, TAG_LIMIT, 'popular')
                ]);

                if (!isActive) {
                    return;
                }

                const videos = videoResult.status === 'fulfilled'
                    ? videoResult.value.Results ?? []
                    : [];
                const tags = tagResult.status === 'fulfilled'
                    ? tagResult.value ?? []
                    : [];
                const hasAnySuccess = videoResult.status === 'fulfilled' || tagResult.status === 'fulfilled';

                setSuggestions({
                    status: hasAnySuccess ? 'success' : 'error',
                    videos,
                    tags,
                    fighters: buildDerivedFighters(videos, textQuery),
                    events: buildDerivedEvents(videos, textQuery)
                });
            };

            void loadSuggestions();
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            isActive = false;
            window.clearTimeout(handler);
        };
    }, [query]);

    return suggestions;
};
